// The roster: people down the side, seven days across.
//
// Two layouts of the same data, because a person × day matrix does not survive
// a phone. Wide screens get the grid — the shape a manager needs, where the
// hole in Thursday is visible without scrolling. Narrow screens get the same
// week stacked by day, which is the shape somebody standing in the aisle
// actually reads.
//
// A cell holds three different things and they are deliberately not equal:
//
//   shifts     what this is for. Solid, colored, clickable.
//   time off   why the cell is empty, or why it shouldn't be full.
//   lessons    context only, drawn faintly. You cannot edit a lesson from here
//              and it does not belong to the roster — it is there so nobody
//              gets put on evening feed while they are teaching.

import { colorClass } from '../../lib/lessonTypes';
import { personClass, assignPersonColors } from '../../lib/people';
import { fmtDate } from '../../lib/dates';
import {
  byCell,
  cellKey,
  gridRows,
  rowMinutes,
  columnMinutes,
  countFor,
  peopleOn,
  shiftsLabel,
  formatMinutes,
  timeRange,
  timeOffOn,
  lessonsOn,
  worstConflict,
  clock,
} from '../../lib/shifts';

function dayLabel(iso) {
  return fmtDate(iso, 'weekday');
}
function dayNumber(iso) {
  return fmtDate(iso, 'numeric');
}

export default function ShiftGrid({
  days,
  today,
  shifts,
  staff,
  timeOff,
  lessons,
  // One roll-up per day that has a routine applied: what it needs against what
  // it has. Absent for a day nobody has planned, which is not the same as a day
  // that needs nothing — so an unplanned column says how many are on and stops.
  coverage = [],
  canEdit,
  onAdd,
  onOpen,
}) {
  const coverFor = (d) => coverage.find((c) => c.date === d) || null;
  const rows = gridRows(staff, shifts);
  const cells = byCell(shifts);
  // Handed out across the whole roster, including anyone with a shift who has
  // since left the staff list, so a colour on the page is never shared while
  // there are six or fewer people to colour.
  const colors = assignPersonColors([...staff.map((s) => s.userId), ...shifts.map((s) => s.userId)]);

  if (!rows.length) {
    return (
      <p className="card p-6 text-sm text-ink-400">
        Nobody on the team can be scheduled yet. Add people under More → Team first — clients and riders
        don&apos;t appear here.
      </p>
    );
  }

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* Wide: the matrix.                                                */}
      {/* Below lg a column is too narrow for a name and a time, which is  */}
      {/* the measurement that moved the board's week to xl as well.       */}
      {/* ---------------------------------------------------------------- */}
      <div className="hidden lg:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-ink-200">
                <th className="text-left px-3 py-2 w-44 text-xs font-medium uppercase tracking-wider text-ink-400">
                  Team member
                </th>
                {days.map((d) => (
                  <th
                    key={d}
                    className={
                      'px-2 py-2 text-left text-sm font-semibold align-bottom ' +
                      (d === today ? 'bg-hunter/5' : '')
                    }
                  >
                    <span className={d === today ? 'text-hunter' : 'text-ink-800'}>{dayLabel(d)}</span>{' '}
                    <span className="font-normal text-ink-400">{dayNumber(d)}</span>
                    {coverFor(d)?.shortCount > 0 && (
                      <span
                        className="block mt-0.5 text-[11px] font-normal text-amber-700"
                        title="Somebody is missing from this day"
                      >
                        short {coverFor(d).needed - coverFor(d).covered}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b border-ink-100 last:border-0 align-top">
                  <th scope="row" className="text-left px-3 py-2 w-44">
                    <span
                      className={
                        'block text-sm font-medium break-words ' +
                        (row.open ? 'text-amber-700' : 'text-ink-900')
                      }
                    >
                      {row.name}
                    </span>
                    <span className="block text-xs text-ink-400">
                      {formatMinutes(rowMinutes(shifts, row.userId))} ·{' '}
                      {shiftsLabel(countFor(shifts, row.userId))}
                      {row.role ? ` · ${row.role}` : ''}
                    </span>
                  </th>
                  {days.map((d) => (
                    <td
                      key={d}
                      className={'px-1.5 py-1.5 min-w-[8rem] ' + (d === today ? 'bg-hunter/5' : '')}
                    >
                      <Cell colors={colors}
                        date={d}
                        row={row}
                        shifts={cells.get(cellKey(row.userId, d)) || []}
                        timeOff={row.userId ? timeOffOn(timeOff, row.userId, d) : []}
                        lessons={row.userId ? lessonsOn(lessons, row.userId, d) : []}
                        canEdit={canEdit}
                        onAdd={onAdd}
                        onOpen={onOpen}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-ink-200 bg-ink-50">
                {/* A total, not a column heading — so not uppercased. "26H 30M"
                    read as a label rather than the number it is. */}
                <td className="px-3 py-2 text-xs font-medium text-ink-600 tabular-nums">
                  {formatMinutes(shifts.reduce((s, x) => s + (x.minutes || 0), 0))} ·{' '}
                  {shiftsLabel(shifts.length)}
                </td>
                {days.map((d) => {
                  const on = peopleOn(shifts, d);
                  const cov = coverFor(d);
                  return (
                    <td key={d} className="px-2 py-2 text-xs text-ink-500 tabular-nums">
                      {formatMinutes(columnMinutes(shifts, d))}
                      {cov ? (
                        <span
                          className={
                            'block ' + (cov.shortCount > 0 ? 'text-amber-700 font-medium' : 'text-hunter')
                          }
                          title={
                            cov.shortCount > 0
                              ? `${cov.shortCount} of the day's jobs ${cov.shortCount === 1 ? 'is' : 'are'} short`
                              : 'Everything this day needs is covered'
                          }
                        >
                          {cov.covered} of {cov.needed} covered
                        </span>
                      ) : (
                        on > 0 && <span className="text-ink-400"> · {on} on</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Narrow: the same week, one day at a time.                        */}
      {/* ---------------------------------------------------------------- */}
      <div className="lg:hidden space-y-3">
        {days.map((d) => {
          const onThisDay = shifts.filter((s) => s.date === d);
          return (
            <section key={d} className={'card p-3 ' + (d === today ? 'border-hunter bg-hunter/5' : '')}>
              <header className="flex items-baseline justify-between pb-2 mb-2 border-b border-ink-100">
                <h3 className="text-sm font-semibold">
                  <span className={d === today ? 'text-hunter' : ''}>{dayLabel(d)}</span>{' '}
                  <span className="text-ink-400 font-normal">{dayNumber(d)}</span>
                </h3>
                <span className="text-[11px] text-ink-400">
                  {formatMinutes(columnMinutes(shifts, d))}
                  {coverFor(d) ? (
                    <span className={coverFor(d).shortCount > 0 ? ' text-amber-700 font-medium' : ' text-hunter'}>
                      {' · '}
                      {coverFor(d).covered} of {coverFor(d).needed} covered
                    </span>
                  ) : (
                    peopleOn(shifts, d) > 0 && ` · ${peopleOn(shifts, d)} on`
                  )}
                </span>
              </header>

              {/* Who is away, which the wide grid puts in each person's cell.
                  Without it the stacked view shows a warning ring and no
                  reason — and a phone has no hover to reveal one. */}
              {offToday(timeOff, d).length > 0 && (
                <ul className="mb-2 space-y-1">
                  {offToday(timeOff, d).map((o) => (
                    <li
                      key={o.id}
                      className={
                        'rounded-md px-2 py-1 text-[11px] border ' +
                        (o.status === 'approved'
                          ? 'bg-sky-50 border-sky-200 text-sky-800'
                          : 'bg-amber-50 border-amber-200 text-amber-800')
                      }
                    >
                      {/* users.name is nullable, and a line that opens with
                          an em dash reads as a typo rather than a gap. */}
                      {o.name || 'Unnamed'}
                      {o.status === 'approved'
                        ? ` — off ${o.allDay ? 'all day' : timeRange(o.startTime, o.endTime)}`
                        : ' — asked for this off'}
                      {o.reason ? ` · ${o.reason}` : ''}
                    </li>
                  ))}
                </ul>
              )}

              {onThisDay.length === 0 ? (
                <p className="text-xs text-ink-400 py-1">Nobody scheduled.</p>
              ) : (
                <ul className="space-y-1.5">
                  {onThisDay
                    .slice()
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map((s) => (
                      <li key={s.id}>
                        <ShiftBlock colors={colors} shift={s} showName showDetail onOpen={onOpen} canEdit={canEdit} />
                      </li>
                    ))}
                </ul>
              )}

              {canEdit && (
                <button
                  onClick={() => onAdd(d, null)}
                  className="mt-2 w-full rounded-lg border border-dashed border-ink-200 py-1.5 text-xs text-ink-400 hover:text-ink-700 hover:border-ink-300"
                >
                  + Add shift
                </button>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

function Cell({ date, row, shifts, timeOff, lessons, colors, canEdit, onAdd, onOpen }) {
  const approvedOff = timeOff.find((o) => o.status === 'approved');
  const requestedOff = timeOff.find((o) => o.status === 'pending');

  return (
    <div className="space-y-1">
      {approvedOff && (
        // Sky: away from the barn. The same color the horse cards use for a
        // horse that isn't here today.
        <p className="rounded-md bg-sky-50 border border-sky-200 px-2 py-1 text-[11px] text-sky-800 leading-snug">
          {approvedOff.allDay ? 'Off all day' : `Off ${timeRange(approvedOff.startTime, approvedOff.endTime)}`}
          {approvedOff.reason ? ` · ${approvedOff.reason}` : ''}
        </p>
      )}
      {!approvedOff && requestedOff && (
        <p className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800 leading-snug">
          Asked for this off
        </p>
      )}

      {shifts.map((s) => (
        <ShiftBlock colors={colors} key={s.id} shift={s} onOpen={onOpen} canEdit={canEdit} />
      ))}

      {lessons.length > 0 && (
        // Faint and unclickable on purpose: this is not the roster, it is the
        // reason a cell should or shouldn't be filled.
        <p className="px-2 text-[11px] text-ink-400 leading-snug">
          Teaching {clock(lessons[0].startTime)}
          {lessons.length > 1 ? ` +${lessons.length - 1} more` : ''}
        </p>
      )}

      {canEdit && (
        // Thirty-five dashed boxes is what the grid looked like before: the
        // affordance shouted louder than the schedule. A faint glyph that
        // becomes a target on hover keeps it findable without competing.
        // The transparent border is always there so nothing shifts on hover.
        <button
          type="button"
          onClick={() => onAdd(date, row.userId)}
          className={
            'w-full rounded-md border border-transparent py-1 text-[11px] leading-none ' +
            'text-ink-200 hover:text-ink-700 hover:border-dashed hover:border-ink-300 ' +
            'focus:text-ink-700 focus:border-dashed focus:border-ink-300'
          }
          aria-label={`Add a shift for ${row.name} on ${date}`}
        >
          +
        </button>
      )}
    </div>
  );
}

/** Everyone off on one day, approved first — the stacked view's header line. */
function offToday(timeOff, date) {
  return timeOff
    .filter((o) => date >= o.startDate && date <= o.endDate)
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'approved' ? -1 : 1));
}

function ShiftBlock({ shift, colors, showName = false, showDetail = false, canEdit, onOpen }) {
  const conflict = worstConflict(shift);
  // The block is the PERSON's colour, and the kind of work is a dot and a word
  // inside it. It was the other way round, which answered "who is on morning
  // feed" and not "when is Taylor here" — and it is the second question a
  // phone gets asked in an aisle. An open shift is white: it is a hole, and a
  // hole should not wear anybody's colour.
  const tone = shift.userId ? personClass(shift.userId, 'card', colors) : 'bg-white border-dashed border-ink-300 text-ink-700';

  return (
    <button
      type="button"
      onClick={() => onOpen(shift)}
      disabled={!canEdit}
      className={
        'w-full text-left rounded-md border px-2 py-1.5 leading-snug ' +
        tone +
        (canEdit ? ' hover:brightness-95 cursor-pointer' : ' cursor-default') +
        // A ring rather than a different fill: the shift's own color still has
        // to say what kind of work it is.
        (conflict?.severity === 'blocking' ? ' ring-2 ring-rose-400' : '')
      }
      title={conflict?.detail || shift.notes || undefined}
    >
      <span className="flex items-start gap-1">
        <span className="min-w-0 flex-1">
          {showName && (
            <span className="block text-xs font-medium break-words">
              {shift.name || 'Open shift'}
            </span>
          )}
          <span className="block text-[11px] tabular-nums">{timeRange(shift.startTime, shift.endTime)}</span>
          {shift.typeName && (
            <span className="block text-[11px] opacity-80 break-words">
              <span
                className={'inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ' + colorClass(shift.color, 'dot')}
                aria-hidden
              />
              {shift.typeName}
            </span>
          )}
          {/* The wide grid can hang this off a title attribute; a phone can't,
              so where there is room the reason is written out. */}
          {showDetail && conflict && (
            <span
              className={
                'block text-[11px] mt-0.5 break-words ' +
                (conflict.severity === 'blocking' ? 'text-rose-700' : 'text-amber-700')
              }
            >
              {conflict.detail}
            </span>
          )}
        </span>
        <span className="flex flex-col items-end gap-0.5 shrink-0 pt-0.5">
          {/* An amber dot means "the barn doesn't know about this yet" — a
              draft shift and an edited-but-unannounced one are the same fact
              to whoever is about to press Publish. */}
          {shift.unannounced && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-500"
              aria-label="Not announced yet"
              title="Not announced yet"
            />
          )}
          {conflict && (
            <span
              className={conflict.severity === 'blocking' ? 'text-rose-600' : 'text-amber-600'}
              aria-label={conflict.detail}
            >
              ⚠
            </span>
          )}
        </span>
      </span>
    </button>
  );
}
